import React, { useEffect, useState, useMemo } from "react";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import OrderInvoiceModal from "./OrderInvoiceModal";
import ShippingPanel from "./ShippingPanel";
import PickupModal from "./PickupModal";
import {
  Block,
  BlockHeadContent,
  BlockTitle,
  BlockBetween,
  BlockHead,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  Icon,
  PaginationComponent,
  PreviewAltCard,
  Row,
  Col,
} from "../../components/Component";
import {
  UncontrolledDropdown,
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Badge,
  Spinner,
} from "reactstrap";
import { handleGetApis, handlePostApis } from "../../service/Api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const PAYMENT_COLOR = { paid: "success", pending: "warning", failed: "danger" };

const STATUS_OPTIONS = [
  { value: "",           label: "All Statuses" },
  { value: "Created",    label: "Created"      },
  { value: "Confirmed",  label: "Confirmed"    },
  { value: "Processing", label: "Processing"   },
  { value: "Shipped",    label: "Shipped"      },
  { value: "Delivered",  label: "Delivered"    },
  { value: "Cancelled",  label: "Cancelled"    },
];

// Next allowed statuses from current status
const STATUS_TRANSITIONS = {
  Created:   ["Confirmed", "Cancelled"],
  Confirmed: ["Processing", "Shipped", "Cancelled"],
  // Processing = packed / AWB created; Delhivery tracking moves it on by itself.
  Processing: ["Shipped",  "Cancelled"],
  Shipped:   ["Delivered", "Cancelled"],
  Delivered: [],   // terminal
  Cancelled: [],   // terminal
};

// Single endpoint — order_status sent in body
const STATUS_ENDPOINT = (id) => `admin/order/changeStatus/${id}`;

// Badge color per status
const STATUS_COLOR = {
  Created:   "warning",
  Confirmed: "info",
  Processing: "secondary",
  Shipped:   "primary",
  Delivered: "success",
  Cancelled: "danger",
};

// Icon per status
const STATUS_ICON = {
  Created:   "clock",
  Confirmed: "check-circle",
  Processing: "box",
  Shipped:   "truck",
  Delivered: "package",
  Cancelled: "cross-circle",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatOrderDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

// ─── Component ────────────────────────────────────────────────────────────────

const OrderList = () => {
  // data
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(true);

  // filters
  const [searchText, setSearchText]     = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom]         = useState(null);
  const [dateTo, setDateTo]             = useState(null);

  // UI
  const [smOption, setSmOption]         = useState(false);
  const [currentPage, setCurrentPage]   = useState(1);
  const [itemPerPage]                   = useState(10);

  // detail modal
  const [detailOrder, setDetailOrder]   = useState(null);
  const [detailOpen, setDetailOpen]     = useState(false);

  // invoice modal
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [invoiceOpen, setInvoiceOpen]   = useState(false);

  // status change modal
  const [statusModal, setStatusModal]   = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);   // { order, newStatus }
  const [statusLoading, setStatusLoading] = useState(false);

  // shipping (Delhivery)
  const [shippingConfig, setShippingConfig] = useState(null);
  const [pickupOpen, setPickupOpen]         = useState(false);

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchOrders = async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const res = await handleGetApis("admin/get/orders");
      if (res?.data?.success) {
        setData(res.data.data);
        return res.data.data;
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
    return null;
  };

  const fetchShippingConfig = async () => {
    const res = await handleGetApis("admin/shipping/config");
    if (res?.data?.success) setShippingConfig(res.data.data);
  };

  useEffect(() => { fetchOrders(); fetchShippingConfig(); }, []);

  // After a shipping action: reload the list and keep the open order in sync.
  const refreshAfterShipping = async () => {
    const orders = await fetchOrders({ quiet: true });
    const fresh = orders?.find((o) => o.id === detailOrder?.id);
    if (fresh) setDetailOrder(fresh);
    fetchShippingConfig();
  };

  // ── Multi-filter (memoised) ───────────────────────────────────────────────
  const filterData = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return data.filter((item) => {
      if (q) {
        const refMatch   = item.order_ref?.toLowerCase().includes(q);
        const nameMatch  = item.customer?.name?.toLowerCase().includes(q);
        const phoneMatch = item.customer?.phone?.toLowerCase().includes(q);
        if (!refMatch && !nameMatch && !phoneMatch) return false;
      }
      if (statusFilter && item.order_status?.toLowerCase() !== statusFilter.toLowerCase()) return false;
      if (dateFrom) {
        const from = new Date(dateFrom); from.setHours(0, 0, 0, 0);
        if (new Date(item.created_at) < from) return false;
      }
      if (dateTo) {
        const to = new Date(dateTo); to.setHours(23, 59, 59, 999);
        if (new Date(item.created_at) > to) return false;
      }
      return true;
    });
  }, [data, searchText, statusFilter, dateFrom, dateTo]);

  // Reset page on filter change
  useEffect(() => { setCurrentPage(1); }, [searchText, statusFilter, dateFrom, dateTo]);

  // ── Pagination ────────────────────────────────────────────────────────────
  const indexOfLastItem  = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems     = filterData.slice(indexOfFirstItem, indexOfLastItem);
  const paginate = (p) => setCurrentPage(p);

  // ── Detail Modal ──────────────────────────────────────────────────────────
  const openDetail = (order) => { setDetailOrder(order); setDetailOpen(true); };
  const closeDetail = () => setDetailOpen(false);

  // ── Invoice Modal ─────────────────────────────────────────────────────────
  const openInvoice = (order) => { setInvoiceOrder(order); setInvoiceOpen(true); };
  const closeInvoice = () => setInvoiceOpen(false);

  // ── Status Change ─────────────────────────────────────────────────────────
  const openStatusModal = (order, newStatus) => {
    setStatusTarget({ order, newStatus });
    setStatusModal(true);
  };
  const closeStatusModal = () => {
    setStatusModal(false);
    setStatusTarget(null);
  };

  const confirmStatusChange = async () => {
    if (!statusTarget) return;
    const { order, newStatus } = statusTarget;

    setStatusLoading(true);
    try {
      const res = await handlePostApis(STATUS_ENDPOINT(order.id), { order_status: newStatus });
      if (res?.data?.success || res?.status === 200) {
        // Optimistic update
        setData((prev) =>
          prev.map((o) =>
            o.id === order.id ? { ...o, order_status: newStatus } : o
          )
        );
        // Also update detail modal if open
        if (detailOrder?.id === order.id) {
          setDetailOrder((prev) => ({ ...prev, order_status: newStatus }));
        }
        // The API says whether the confirmation email reached the customer.
        const message = res.data?.message || `Order marked as ${newStatus}`;
        const emailFailed = /could not be sent/i.test(message);
        (emailFailed ? toast.warning : toast.success)(`#${order.order_ref}: ${message}`);
        closeStatusModal();
      } else {
        toast.error(res?.data?.message || "Failed to update status");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setStatusLoading(false);
    }
  };

  // ── Reset Filters ─────────────────────────────────────────────────────────
  const resetFilters = () => {
    setSearchText(""); setStatusFilter("");
    setDateFrom(null); setDateTo(null);
  };
  const isFiltered = searchText || statusFilter || dateFrom || dateTo;

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <React.Fragment>
      <Head title="Order List"></Head>
      <ToastContainer position="top-right" autoClose={3000} />

      <Content>
        {/* ── Header ───────────────────────────────────────────────── */}
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle>
                Orders{" "}
                <small className="text-muted fs-14px">
                  ({filterData.length} {isFiltered ? "filtered" : "total"})
                </small>
              </BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              {shippingConfig?.enabled && (
                <Button color="primary" outline size="sm" onClick={() => setPickupOpen(true)}>
                  <Icon name="truck" />
                  <span>Schedule Pickup{shippingConfig.pending_pickup ? ` (${shippingConfig.pending_pickup})` : ""}</span>
                </Button>
              )}
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <a
                  href="#more"
                  className="btn btn-icon btn-trigger toggle-expand me-n1"
                  onClick={(ev) => { ev.preventDefault(); setSmOption(!smOption); }}
                >
                  <Icon name="more-v"></Icon>
                </a>
                
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          {/* ── Filter Card ──────────────────────────────────────────── */}
          <div className="card card-bordered mb-3">
            <div className="card-inner">
              <Row className="g-3 align-items-end">
                <Col sm={12} md={3}>
                  <label className="form-label">Search</label>
                  <div className="form-control-wrap">
                    <div className="form-icon form-icon-right"><Icon name="search"></Icon></div>
                    <input
                      type="text" className="form-control"
                      placeholder="Order ref, name, phone..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  </div>
                </Col>
                <Col sm={6} md={2}>
                  <label className="form-label">Status</label>
                  <select
                    className="form-select form-control"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </Col>
                <Col sm={6} md={2}>
                  <label className="form-label">From Date</label>
                  <DatePicker
                    selected={dateFrom}
                    onChange={(date) => setDateFrom(date)}
                    selectsStart startDate={dateFrom} endDate={dateTo}
                    maxDate={dateTo || new Date()}
                    placeholderText="dd/mm/yyyy" dateFormat="dd/MM/yyyy"
                    className="form-control" isClearable
                  />
                </Col>
                <Col sm={6} md={2}>
                  <label className="form-label">To Date</label>
                  <DatePicker
                    selected={dateTo}
                    onChange={(date) => setDateTo(date)}
                    selectsEnd startDate={dateFrom} endDate={dateTo}
                    minDate={dateFrom} maxDate={new Date()}
                    placeholderText="dd/mm/yyyy" dateFormat="dd/MM/yyyy"
                    className="form-control" isClearable
                  />
                </Col>
                <Col sm={6} md={1}>
                  {isFiltered && (
                    <Button color="light" outline onClick={resetFilters} className="w-100">
                      <Icon name="cross-circle" /> <span>Reset</span>
                    </Button>
                  )}
                </Col>

                {/* Active tags */}
                {isFiltered && (
                  <Col sm={12}>
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      <span className="text-soft fs-12px">Active filters:</span>
                      {searchText && (
                        <span className="badge bg-outline-primary rounded-pill">
                          Search: "{searchText}"
                          <a href="#rm" className="ms-1" onClick={(e) => { e.preventDefault(); setSearchText(""); }}>✕</a>
                        </span>
                      )}
                      {statusFilter && (
                        <span className="badge bg-outline-info rounded-pill">
                          Status: {statusFilter}
                          <a href="#rm" className="ms-1" onClick={(e) => { e.preventDefault(); setStatusFilter(""); }}>✕</a>
                        </span>
                      )}
                      {dateFrom && (
                        <span className="badge bg-outline-warning rounded-pill">
                          From: {dateFrom.toLocaleDateString("en-IN")}
                          <a href="#rm" className="ms-1" onClick={(e) => { e.preventDefault(); setDateFrom(null); }}>✕</a>
                        </span>
                      )}
                      {dateTo && (
                        <span className="badge bg-outline-warning rounded-pill">
                          To: {dateTo.toLocaleDateString("en-IN")}
                          <a href="#rm" className="ms-1" onClick={(e) => { e.preventDefault(); setDateTo(null); }}>✕</a>
                        </span>
                      )}
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          </div>

          {/* ── Table ─────────────────────────────────────────────────── */}
          <div className="nk-tb-list is-separate is-medium mb-3">
            <DataTableHead className="nk-tb-item">
              <DataTableRow><span className="sub-text">Order</span></DataTableRow>
              <DataTableRow size="md"><span className="sub-text">Date</span></DataTableRow>
              <DataTableRow><span className="sub-text">Status</span></DataTableRow>
              <DataTableRow><span className="sub-text">Payment</span></DataTableRow>
              <DataTableRow size="sm"><span className="sub-text">Customer</span></DataTableRow>
              <DataTableRow size="md"><span className="sub-text">Phone</span></DataTableRow>
              <DataTableRow><span className="sub-text">Total</span></DataTableRow>
              <DataTableRow><span className="sub-text">Action</span></DataTableRow>
            </DataTableHead>

            {loading ? (
              <div className="text-center py-4">
                <Spinner size="sm" color="primary" className="me-2" />
                <span className="text-soft">Loading orders...</span>
              </div>
            ) : currentItems.length > 0 ? (
              currentItems.map((item) => {
                const nextStatuses = STATUS_TRANSITIONS[item.order_status] || [];
                const isTerminal   = nextStatuses.length === 0;
                return (
                  <DataTableItem key={item.id}>
                    <DataTableRow>
                      <a href="#id" onClick={(ev) => { ev.preventDefault(); openDetail(item); }}>
                        #{item.order_ref}
                      </a>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span>{formatOrderDate(item.created_at)}</span>
                    </DataTableRow>
                    <DataTableRow>
                      <Badge
                        className="badge-sm badge-dot has-bg d-inline-flex"
                        color={STATUS_COLOR[item.order_status] || "secondary"}
                      >
                        {item.order_status}
                      </Badge>
                      {item.tracking_id && (
                        <span className="d-block text-soft fs-11px mt-1" title={item.shipment_status || ""}>
                          AWB {item.tracking_id}{item.shipment_status ? ` · ${item.shipment_status}` : ""}
                        </span>
                      )}
                    </DataTableRow>
                    <DataTableRow>
                      <Badge
                        className="badge-sm has-bg d-inline-flex"
                        color={PAYMENT_COLOR[item.payment_status] || "secondary"}
                      >
                        {item.payment_status === "paid" ? "Paid" : item.payment_status === "pending" ? "Not paid" : item.payment_status}
                      </Badge>
                    </DataTableRow>
                    <DataTableRow size="sm">
                      <span className="tb-sub">{item.customer?.name}</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span className="tb-sub text-primary">{item.customer?.phone}</span>
                    </DataTableRow>
                    <DataTableRow>
                      <span className="tb-lead">₹ {Number(item.total).toLocaleString("en-IN")}</span>
                    </DataTableRow>
                    <DataTableRow className="nk-tb-col-tools">
                      <ul className="nk-tb-actions gx-1">
                        <li>
                          <UncontrolledDropdown>
                            <DropdownToggle tag="a" className="btn btn-icon dropdown-toggle btn-trigger">
                              <Icon name="more-h"></Icon>
                            </DropdownToggle>
                            <DropdownMenu end>
                              <ul className="link-list-opt no-bdr">
                                {/* View Details */}
                                <li>
                                  <DropdownItem tag="a" href="#view"
                                    onClick={(ev) => { ev.preventDefault(); openDetail(item); }}
                                  >
                                    <Icon name="eye"></Icon>
                                    <span>Order Details</span>
                                  </DropdownItem>
                                </li>
                                {/* Invoice */}
                                <li>
                                  <DropdownItem tag="a" href="#invoice"
                                    onClick={(ev) => { ev.preventDefault(); openInvoice(item); }}
                                  >
                                    <Icon name="file-text"></Icon>
                                    <span>View Invoice</span>
                                  </DropdownItem>
                                </li>

                                {/* Status Change Section */}
                                {!isTerminal && (
                                  <>
                                    <li className="divider"></li>
                                    <li>
                                      <span
                                        style={{
                                          padding: "6px 18px",
                                          display: "block",
                                          fontSize: 11,
                                          fontWeight: 600,
                                          textTransform: "uppercase",
                                          letterSpacing: "0.08em",
                                          color: "#8094ae",
                                        }}
                                      >
                                        Change Status
                                      </span>
                                    </li>
                                    {nextStatuses.map((ns) => (
                                      <li key={ns}>
                                        <DropdownItem tag="a" href={`#${ns}`}
                                          onClick={(ev) => { ev.preventDefault(); openStatusModal(item, ns); }}
                                        >
                                          <Icon name={STATUS_ICON[ns]}></Icon>
                                          <span>
                                            Mark as{" "}
                                            <strong
                                              style={{ color: `var(--bs-${STATUS_COLOR[ns] === "warning" ? "warning" : STATUS_COLOR[ns]})` }}
                                            >
                                              {ns}
                                            </strong>
                                          </span>
                                        </DropdownItem>
                                      </li>
                                    ))}
                                  </>
                                )}
                              </ul>
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        </li>
                      </ul>
                    </DataTableRow>
                  </DataTableItem>
                );
              })
            ) : (
              <div className="text-center py-4">
                <Icon name="inbox" style={{ fontSize: 32, opacity: 0.3 }} />
                <p className="text-soft mt-2 mb-0">No orders found</p>
                {isFiltered && (
                  <a href="#reset" onClick={(e) => { e.preventDefault(); resetFilters(); }} className="link mt-1">
                    Clear filters
                  </a>
                )}
              </div>
            )}
          </div>

          {/* ── Pagination ───────────────────────────────────────────── */}
          <PreviewAltCard>
            {filterData.length > 0 ? (
              <PaginationComponent
                itemPerPage={itemPerPage}
                totalItems={filterData.length}
                paginate={paginate}
                currentPage={currentPage}
              />
            ) : null}
          </PreviewAltCard>
        </Block>

        {/* ── Invoice Modal ───────────────────────────────────────────── */}
        <OrderInvoiceModal
          isOpen={invoiceOpen}
          toggle={closeInvoice}
          order={invoiceOrder}
        />

        {/* ── Delhivery Pickup Modal ───────────────────────────────────── */}
        {pickupOpen && (
          <PickupModal isOpen={pickupOpen} toggle={() => setPickupOpen(false)} config={shippingConfig} />
        )}

        {/* ── Status Change Confirmation Modal ────────────────────────── */}
        {statusModal && statusTarget && (
          <Modal isOpen={statusModal} toggle={closeStatusModal} className="modal-dialog-centered" size="sm">
            <ModalHeader toggle={closeStatusModal}>Confirm Status Change</ModalHeader>
            <ModalBody>
              <div className="text-center py-2">
                <div
                  className="mb-3"
                  style={{
                    width: 64, height: 64, borderRadius: "50%",
                    background: `rgba(var(--bs-${STATUS_COLOR[statusTarget.newStatus]}-rgb, 101,118,255), 0.12)`,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <Icon
                    name={STATUS_ICON[statusTarget.newStatus]}
                    style={{ fontSize: 28, color: `var(--bs-${STATUS_COLOR[statusTarget.newStatus]})` }}
                  />
                </div>
                <p className="mb-1">
                  Change order{" "}
                  <strong>#{statusTarget.order.order_ref}</strong> status to:
                </p>
                <Badge color={STATUS_COLOR[statusTarget.newStatus]} className="fs-14px px-3 py-1">
                  {statusTarget.newStatus}
                </Badge>
                <p className="text-soft mt-3 mb-0 fs-13px">
                  Current:{" "}
                  <Badge color={STATUS_COLOR[statusTarget.order.order_status]} className="ms-1">
                    {statusTarget.order.order_status}
                  </Badge>
                </p>
                {statusTarget.newStatus === "Confirmed" && statusTarget.order.customer?.email && (
                  <p className="text-soft mt-2 mb-0 fs-13px">
                    The customer will be emailed a confirmation with the invoice PDF.
                  </p>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="light" onClick={closeStatusModal} disabled={statusLoading}>
                Cancel
              </Button>
              <Button
                color={STATUS_COLOR[statusTarget.newStatus] || "primary"}
                onClick={confirmStatusChange}
                disabled={statusLoading}
              >
                {statusLoading ? (
                  <><Spinner size="sm" className="me-1" /> Updating...</>
                ) : (
                  <>
                    <Icon name={STATUS_ICON[statusTarget.newStatus]} className="me-1" />
                    Yes, Mark as {statusTarget.newStatus}
                  </>
                )}
              </Button>
            </ModalFooter>
          </Modal>
        )}

        {/* ── Order Detail Modal ──────────────────────────────────────── */}
        {detailOpen && detailOrder && (
          <Modal isOpen={detailOpen} toggle={closeDetail} className="modal-dialog-top" size="lg">
            <ModalHeader toggle={closeDetail} className="border-bottom">
              <div className="pe-3 pe-sm-0 d-flex align-items-center gap-2 flex-wrap">
                <span className="fw-bold fs-16px text-dark">Order Details</span>
                <span className="text-soft fs-13px">#{detailOrder.order_ref}</span>
                <Badge color={STATUS_COLOR[detailOrder.order_status] || "secondary"} className="badge-dim rounded-pill text-capitalize px-2 py-1 fs-12px">
                  <Icon name={STATUS_ICON[detailOrder.order_status]} className="me-1" />
                  {detailOrder.order_status}
                </Badge>
              </div>
            </ModalHeader>
            <ModalBody className="p-3 p-sm-4">
              <div>
                {/* ── Order & Customer Info Grid ── */}
                <Row className="g-3 mb-3 mb-sm-4">
                  <Col sm="6" xs="12">
                    <div className="bg-light p-3 rounded h-100">
                      <span className="text-soft fs-11px fw-bold text-uppercase d-block mb-2">Order Information</span>
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted fs-12px">Date:</span>
                        <span className="text-dark fs-12px fw-bold">{formatOrderDate(detailOrder.created_at)}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted fs-12px">Payment:</span>
                        <div className="d-flex align-items-center gap-1">
                          <span className="text-dark fs-12px fw-bold">
                            {detailOrder.payment_method === "cod" ? "Cash on Delivery" : "Online"}
                          </span>
                          <Badge
                            className="badge-sm badge-dot has-bg ms-1"
                            color={detailOrder.payment_status === "paid" ? "success" : "warning"}
                          >
                            {detailOrder.payment_status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col sm="6" xs="12">
                    <div className="bg-light p-3 rounded h-100">
                      <span className="text-soft fs-11px fw-bold text-uppercase d-block mb-2">Customer &amp; Delivery</span>
                      <div className="text-dark fs-13px fw-bold mb-1">{detailOrder.customer?.name || "—"}</div>
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        {detailOrder.customer?.phone && (
                          <a href={`tel:${detailOrder.customer.phone}`} className="text-muted fs-12px d-inline-flex align-items-center">
                            <Icon name="call" className="me-1 text-primary" />{detailOrder.customer.phone}
                          </a>
                        )}
                        {detailOrder.customer?.email && (
                          <a href={`mailto:${detailOrder.customer.email}`} className="text-muted fs-12px d-inline-flex align-items-center">
                            <Icon name="mail" className="me-1 text-primary" />{detailOrder.customer.email}
                          </a>
                        )}
                      </div>
                      {detailOrder.customer?.address && (
                        <div className="text-soft fs-12px">
                          <Icon name="map-pin" className="me-1" />
                          {detailOrder.customer?.address}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>

                {/* ── Shipping (Delhivery) ── */}
                <ShippingPanel order={detailOrder} config={shippingConfig} onChanged={refreshAfterShipping} />

                {/* ── Ordered Items Table ── */}
                <div className="mb-3 mb-sm-4">
                  <span className="text-soft fs-12px fw-bold text-uppercase d-block mb-2">
                    Ordered Items ({detailOrder?.products?.length || 0})
                  </span>
                  <div className="table-responsive rounded border bg-white">
                    <table className="table table-bordered table-sm mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="py-2 px-2 px-sm-3 text-soft fs-12px" style={{ width: 35 }}>#</th>
                          <th className="py-2 px-2 px-sm-3 text-soft fs-12px text-nowrap">Product Name</th>
                          <th className="py-2 px-2 px-sm-3 text-soft fs-12px text-nowrap">Variant</th>
                          <th className="py-2 px-2 px-sm-3 text-soft fs-12px text-center text-nowrap" style={{ width: 50 }}>Qty</th>
                          <th className="py-2 px-2 px-sm-3 text-soft fs-12px text-end text-nowrap">Price</th>
                          <th className="py-2 px-2 px-sm-3 text-soft fs-12px text-end text-nowrap">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailOrder?.products && detailOrder.products.length > 0 ? (
                          detailOrder.products.map((item, index) => {
                            const subtotal = Number(item?.qty || 1) * Number(item?.price || 0);
                            return (
                              <tr key={index}>
                                <td className="py-2 px-2 px-sm-3 text-soft fs-12px">{index + 1}</td>
                                <td className="py-2 px-2 px-sm-3 fw-bold text-dark fs-13px" style={{ minWidth: 140 }}>
                                  {item?.name || "Product"}
                                </td>
                                <td className="py-2 px-2 px-sm-3 text-soft fs-12px text-nowrap">{item?.variant || "Standard"}</td>
                                <td className="py-2 px-2 px-sm-3 text-center fw-bold text-dark fs-13px">{item?.qty}</td>
                                <td className="py-2 px-2 px-sm-3 text-end fs-13px text-dark text-nowrap">
                                  ₹ {Number(item?.price || 0).toLocaleString("en-IN")}
                                </td>
                                <td className="py-2 px-2 px-sm-3 text-end fw-bold fs-13px text-dark text-nowrap">
                                  ₹ {Number(subtotal).toLocaleString("en-IN")}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="6" className="text-center py-3 text-soft fs-13px">
                              No items found
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot className="border-top bg-light">
                        <tr>
                          <td colSpan="5" className="py-2 px-2 px-sm-3 text-end fw-bold fs-13px text-dark text-nowrap">
                            Total Amount:
                          </td>
                          <td className="py-2 px-2 px-sm-3 text-end fw-bold fs-14px fs-sm-15px text-primary text-nowrap">
                            ₹ {Number(detailOrder.total).toLocaleString("en-IN")}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* ── Action Buttons Footer ── */}
                <div className="pt-3 border-top mt-3 mb-1">
                  <div className="d-flex flex-column flex-sm-row gap-2 align-items-stretch align-items-sm-center justify-content-between">
                    {/* Invoice Button */}
                    <Button
                      size="sm"
                      color="primary"
                      outline
                      className="d-inline-flex align-items-center justify-content-center"
                      onClick={() => { closeDetail(); openInvoice(detailOrder); }}
                    >
                      <Icon name="file-text" className="me-1" />
                      <span>View Invoice</span>
                    </Button>

                    {/* Status change buttons */}
                    {(STATUS_TRANSITIONS[detailOrder.order_status] || []).length > 0 && (
                      <div className="d-flex flex-column flex-sm-row gap-2 align-items-stretch align-items-sm-center">
                        <span className="text-soft fs-11px fw-bold text-uppercase d-none d-sm-inline">Change Status:</span>
                        <div className="d-flex flex-wrap gap-2">
                          {(STATUS_TRANSITIONS[detailOrder.order_status] || []).map((ns) => (
                            <Button
                              key={ns}
                              size="sm"
                              color={STATUS_COLOR[ns]}
                              className="btn-dim d-inline-flex align-items-center justify-content-center flex-grow-1 flex-sm-grow-0"
                              onClick={() => { closeDetail(); openStatusModal(detailOrder, ns); }}
                            >
                              <Icon name={STATUS_ICON[ns]} className="me-1" />
                              <span>Mark as {ns}</span>
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </ModalBody>
          </Modal>
        )}
      </Content>
    </React.Fragment>
  );
};

export default OrderList;
